"use client";

import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../../../constants/config";
import axiosInstance from "../../../utils/axiosInstance";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "react-hot-toast";
import {
  Upload,
  Download,
  AlertCircle,
  CheckCircle,
  FileText,
  Loader,
} from "lucide-react";
import * as XLSX from "xlsx";

export default function ClassroomMappingImport() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState([]);
  const [step, setStep] = useState("upload"); // upload, preview, confirm
  const [mappingData, setMappingData] = useState(null);

  // Fetch import template
  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem("token");

      const response = await axiosInstance.get(
        API_ENDPOINTS.classroomMappingsImportTemplate,
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "classroom-mapping-template.xlsx";
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Template downloaded successfully");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Failed to download template");
    }
  };
  // Handle file selection
  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      // Validate file type
      const allowedTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];

      if (!allowedTypes.includes(selectedFile.type)) {
        toast.error("Please upload an Excel file (.xlsx or .xls)");
        return;
      }

      setFile(selectedFile);
      setErrors([]);
      setPreview(null);
    }
  };

  // Handle file upload and validation
  const handleValidateFile = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    setValidating(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.post(
        API_ENDPOINTS.classroomMappingsImportValidate,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      console.log("Validation response:", response.data);
      if (response.data.status === "success") {
        setPreview(response.data.preview);
        setErrors(response.data.errors || []);
        setMappingData(response.data);
        setStep("preview");
        toast.success(
          `File validated: ${response.data.validRecords.length} valid records found`,
        );
      } else {
        setErrors(response.data.errors || []);
        toast.error("File validation failed");
      }
    } catch (error) {
      console.error("Error validating file:", error);
      const errorMessage =
        error.response?.data?.message || "Error validating file";
      setErrors(error.response?.data?.errors || []);
      toast.error(errorMessage);
    } finally {
      setValidating(false);
    }
  };

  // Handle confirming the import
  const handleConfirmImport = async () => {
    if (!mappingData || !mappingData.validRecords.length) {
      toast.error("No valid records to import");
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.classroomMappingsImportConfirm,
        {
          mappingData: mappingData.validRecords,
        },
      );

      if (response.data.status === "success") {
        toast.success(
          `Import completed: ${response.data.summary.successful} successful, ${response.data.summary.failed} failed`,
        );

        // Reset form
        setFile(null);
        setPreview(null);
        setErrors([]);
        setMappingData(null);
        setStep("upload");

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        // Optionally redirect or refresh
        setTimeout(() => {
          navigate(0); // Refresh the page
        }, 1500);
      }
    } catch (error) {
      console.error("Error confirming import:", error);
      const errorMessage =
        error.response?.data?.message || "Error importing classroom mappings";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setErrors([]);
    setMappingData(null);
    setStep("upload");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-4 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          Classroom Mapping Import
        </h1>
        <p className="text-gray-500 mt-1">
          Import classroom and teacher-subject mappings from Excel
        </p>
      </div>

      {/* Step 1: Upload */}
      {step === "upload" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Step 1: Upload Excel File
            </CardTitle>
            <CardDescription>
              Download the template, fill in your data, and upload the file
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
              <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Excel Format Requirements
              </h3>

              <ul className="text-sm text-blue-800 space-y-2 list-disc list-inside">
                <li>
                  <strong>Classes:</strong> Select Class + Section together
                  (e.g., <span className="font-medium">XI-A</span>)
                </li>

                <li>
                  <strong>Teacher Type:</strong> Choose either
                  <span className="font-medium"> Class Teacher </span>
                  or
                  <span className="font-medium"> Subject Teacher</span>
                </li>

                <li>
                  <strong>Teacher:</strong> Select teacher from dropdown
                  (Format:{" "}
                  <span className="font-medium">Name - EmployeeCode</span>)
                </li>

                <li>
                  <strong>Subjects:</strong> Select subjects using Subject 1 –
                  Subject 10 columns.
                  <br />
                  <span className="text-xs text-blue-700">
                    Duplicate subjects will be automatically cleaned during
                    import.
                  </span>
                </li>

                <li>
                  <strong>All Subjects:</strong> This column is auto-generated.
                  Do not edit manually.
                </li>
              </ul>
            </div>

            {/* Template Download */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex items-center gap-2"
                onClick={downloadTemplate}
              >
                <Download className="w-4 h-4" />
                Download Template
              </Button>

              <p className="text-sm text-gray-500 flex items-center">
                Always use the latest template to avoid validation errors.
              </p>
            </div>

            {/* File Upload */}
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 space-y-3 hover:border-blue-400 transition cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                key={file ? file.name : "empty"}
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
              />

              <div className="text-center">
                <Upload className="w-8 h-8 mx-auto text-gray-400 mb-3" />

                <p className="text-sm font-medium text-gray-700">
                  {file ? file.name : "Click here to upload Excel file"}
                </p>

                <p className="text-xs text-gray-500 mt-1">
                  Supported formats: .xlsx, .xls
                </p>
              </div>
            </div>

            {/* Validation Buttons */}
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={handleReset} disabled={!file}>
                Clear
              </Button>

              <Button
                onClick={handleValidateFile}
                disabled={!file || validating}
                className="flex items-center gap-2"
              >
                {validating && <Loader className="w-4 h-4 animate-spin" />}
                {validating ? "Validating..." : "Validate & Preview"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Preview */}
      {step === "preview" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Step 2: Review Data
            </CardTitle>
            <CardDescription>Review the records to be imported</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">
                      {mappingData?.validRecords.length || 0}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">Valid Records</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-600">
                      {errors.length || 0}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">Errors</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">
                      {mappingData?.totalRecords || 0}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">Total Records</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Errors */}
            {errors.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <p className="font-semibold mb-2">
                    Import Errors ({errors.length}):
                  </p>
                  <div className="space-y-1 max-h-40 overflow-y-auto text-sm">
                    {errors.map((error, idx) => (
                      <p key={idx}>
                        <strong>Row {error.row}:</strong>{" "}
                        {error.error || error.message}
                      </p>
                    ))}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Preview Table */}
            {preview && preview.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Data Preview</h3>

                <div className="overflow-x-auto border rounded-lg">
                  <Table>
                    <TableHeader className="bg-gray-50">
                      <TableRow>
                        <TableHead>Class</TableHead>
                        <TableHead>Section</TableHead>
                        <TableHead>Teacher</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Subjects</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>

                    <TableBody>
                      {preview.slice(0, 10).map((row, idx) => {
                        // Remove duplicate subjects safely
                        const uniqueSubjects = Array.isArray(row.subjects)
                          ? [...new Set(row.subjects)]
                          : [];

                        return (
                          <TableRow
                            key={idx}
                            className="hover:bg-gray-50 transition"
                          >
                            <TableCell className="whitespace-nowrap font-medium">
                              {row.class}
                            </TableCell>

                            <TableCell className="whitespace-nowrap">
                              {row.section}
                            </TableCell>

                            <TableCell className="whitespace-nowrap">
                              {row.teacher}
                            </TableCell>

                            <TableCell>
                              <Badge
                                variant={
                                  row.role?.toLowerCase().includes("class")
                                    ? "default"
                                    : "secondary"
                                }
                              >
                                {row.role}
                              </Badge>
                            </TableCell>

                            <TableCell>
                              <div className="flex flex-wrap gap-1 max-w-[350px]">
                                {uniqueSubjects.length > 0 ? (
                                  uniqueSubjects.map((subject, i) => (
                                    <Badge
                                      key={i}
                                      variant="outline"
                                      className="bg-blue-50 text-blue-700 border-blue-200"
                                    >
                                      {subject}
                                    </Badge>
                                  ))
                                ) : (
                                  <span className="text-gray-400 text-sm">
                                    No subjects
                                  </span>
                                )}
                              </div>
                            </TableCell>

                            <TableCell>
                              <Badge
                                variant="outline"
                                className="bg-green-50 text-green-700 border-green-200"
                              >
                                {row.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {preview.length > 10 && (
                  <p className="text-sm text-gray-500">
                    Showing 10 of {preview.length} records
                  </p>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 justify-end pt-4">
              <Button
                variant="outline"
                onClick={() => setStep("upload")}
                disabled={loading}
              >
                Back to Upload
              </Button>
              <Button
                onClick={handleConfirmImport}
                disabled={loading || !mappingData?.validRecords?.length}
                className="flex items-center gap-2"
              >
                {loading && <Loader className="w-4 h-4 animate-spin" />}
                {loading ? "Importing..." : "Confirm & Import"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Success State */}
      {step === "confirm" && (
        <Card>
          <CardContent className="pt-8">
            <div className="text-center space-y-4">
              <CheckCircle className="w-16 h-16 mx-auto text-green-600" />
              <h2 className="text-2xl font-bold">
                Import Completed Successfully!
              </h2>
              <p className="text-gray-600">
                Classroom mappings have been imported and saved.
              </p>
              <Button onClick={handleReset} className="mt-4">
                Import More Files
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}